import styled, { keyframes } from "styled-components";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { ArrowUpRight, Palette, Paintbrush, Coffee, Plus, X } from "lucide-react";
import bannerVideo from "./Images/videoeltallerdefinitivo-web.mp4";
import painting from "./Images/clientapintando.jpeg";
import event from "./Images/mujeresceramicayvino.png";
import gift from "./Images/taza.jpg.webp";
import studio from "./Images/tallerestanteria.jpeg";
import detail from "./Images/jarraconceramicos.jpeg";
import group from "./Images/familiapintando.jpeg";
import space from "./Images/tallervacio4.jpeg";
import trayFlowers from "./Images/PiezasCreadas/bandeja-flores.jpg";
import greenPlate from "./Images/PiezasCreadas/plato-geometrico-verde.jpg";
import colourfulPlate from "./Images/PiezasCreadas/plato-colorido.jpg";
import finishedPieces from "./Images/PiezasCreadas/piezas-terminadas.jpg";
import paintedGlasses from "./Images/PiezasCreadas/vasos-pintados.jpg";
import pinkBowl from "./Images/PiezasCreadas/cuenco-rosa-lunares.jpg";
import blueBowl from "./Images/PiezasCreadas/cuenco-azul.jpg";
import tableCeramics from "./Images/PiezasCreadas/ceramica-en-mesa.jpg";
import brushProcess from "./Images/PiezasCreadas/proceso-pincel-ceramica.jpg";
import whalePlate from "./Images/PiezasCreadas/plato-ballena.jpg";
import paintedPlates from "./Images/PiezasCreadas/platos-pintados.jpg";
import ceramicProcessOne from "./Images/PiezasCreadas/proceso-ceramica-uno.jpg";
import ceramicProcessTwo from "./Images/PiezasCreadas/proceso-ceramica-dos.jpg";
import decoratingPlate from "./Images/PiezasCreadas/decorando-plato.jpg";
import ceramicPalette from "./Images/PiezasCreadas/paleta-ceramica.jpg";
import kilnWhalePlate from "./Images/PiezasCreadas/plato-ballena-horno.jpg";
import handPinkBowl from "./Images/PiezasCreadas/cuenco-rosa-en-mano.jpg";
import studioInterior from "./Images/PiezasCreadas/cuenco-azul-estanteria.jpg";
import studioShelves from "./Images/PiezasCreadas/cuenco-rosa-estanteria.jpg";
import lemonPlate from "./Images/PiezasCreadas/plato-limones.jpg";
import shelfWhalePlate from "./Images/PiezasCreadas/plato-ballena-estanteria.jpg";
import paintingCeramicsVideo from "./Images/PiezasCreadas/pintando-ceramica.mp4";
import finishedVaseVideo from "./Images/PiezasCreadas/jarron-terminado.mp4";
import finishedCupVideo from "./Images/PiezasCreadas/taza-terminada.mp4";

const enter = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const drift = keyframes`
  to { transform: translateX(-50%); }
`;

const Main = styled.main`
  --ink: #173f35;
  --paper: #f3ecdf;
  --light: #faf6ed;
  --clay: #cf7046;
  --fresh: #d9e7a8;
  background: var(--paper);
  overflow: hidden;
`;

const Hero = styled.section`
  min-height: 100svh;
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: flex-end;
  padding: 7.5rem 1rem 2rem;
  color: #fff;

  @media (min-width: 760px) { padding: 9rem 4vw 4rem; }
  @media (min-width: 1100px) { padding: 10rem 6vw 5.5rem; }
  @media (min-width: 960px) {
    min-height: 92svh;
    padding: 12.5rem 6vw 5rem;
  }
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  z-index: -3;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 54% center;
  @media (min-width: 960px) {
    object-position: center;
    filter: saturate(.86) contrast(1.08);
  }
`;

const HeroShade = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;
  background:
    linear-gradient(180deg, rgba(12, 24, 20, .18), rgba(12, 24, 20, .08) 35%, rgba(12, 24, 20, .77)),
    linear-gradient(90deg, rgba(12, 24, 20, .25), transparent 70%);
  @media (min-width: 960px) {
    background:
      linear-gradient(180deg, rgba(14, 31, 25, .34), rgba(14, 31, 25, .06) 38%, rgba(14, 31, 25, .8)),
      linear-gradient(90deg, rgba(14, 31, 25, .46), rgba(14, 31, 25, .04) 68%);
  }
`;

const HeroGrid = styled.div`
  width: min(100%, 1440px);
  margin: 0 auto;
  display: grid;
  gap: 2rem;
  align-items: end;
  @media (min-width: 850px) { grid-template-columns: 1.45fr .55fr; gap: 4vw; }
  @media (min-width: 960px) { grid-template-columns: minmax(0, 1.28fr) minmax(260px, .62fr); gap: 7vw; }
`;

const HeroCopy = styled.div`
  animation: ${enter} .9s ease both;
`;

const Label = styled.p`
  color: inherit;
  font-family: Arial, sans-serif;
  font-size: .68rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: .16em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  max-width: 8ch;
  margin-top: .85rem;
  color: #fff;
  font-size: clamp(4.2rem, 17vw, 10.5rem);
  font-weight: 450;
  line-height: .79;
  letter-spacing: -.07em;
  @media (min-width: 850px) {
    max-width: 7ch;
    margin-top: 1.1rem;
    font-size: clamp(8rem, 12vw, 15rem);
    line-height: .76;
  }
  @media (min-width: 960px) { font-size: clamp(8.5rem, 11.5vw, 14rem); }
`;

const HeroMeta = styled.div`
  display: grid;
  gap: 1.35rem;
  animation: ${enter} .9s .14s ease both;
`;

const HeroLead = styled.p`
  max-width: 28ch;
  color: rgba(255,255,255,.9);
  font-size: clamp(1.12rem, 3.8vw, 1.45rem);
  line-height: 1.42;
  text-wrap: pretty;
  @media (min-width: 850px) {
    max-width: 24ch;
    font-size: clamp(1.5rem, 2vw, 2.2rem);
    line-height: 1.35;
  }
`;

const PrimaryLink = styled(NavLink)`
  width: fit-content;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  gap: 2rem;
  padding: .85rem 1rem;
  background: var(--fresh);
  border: 1px solid var(--fresh);
  border-radius: 0;
  color: var(--ink);
  font-family: Arial, sans-serif;
  font-size: .76rem;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  transition: background .25s ease, color .25s ease, transform .25s ease;
  &:hover { background: #fff; color: var(--ink); transform: translateY(-3px); }
  @media (min-width: 850px) { min-height: 58px; padding: 1rem 1.25rem; font-size: .82rem; }
`;

const LinkArrow = styled(ArrowUpRight)`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
`;

const LocationLine = styled.div`
  overflow: hidden;
  padding: .8rem 0;
  border-bottom: 1px solid rgba(23,63,53,.25);
  background: var(--fresh);
`;

const MovingLine = styled.div`
  width: max-content;
  display: flex;
  animation: ${drift} 52s linear infinite;
  color: var(--ink);
  font-family: Arial, sans-serif;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .13em;
  text-transform: uppercase;
  white-space: nowrap;

  > span {
    display: flex;
    align-items: center;
  }

  i {
    display: inline-flex;
    align-items: center;
    gap: .7rem;
    margin-right: 1.7rem;
    font-style: normal;
  }

  svg {
    width: 1.05rem;
    height: 1.05rem;
    color: #5f806f;
    stroke-width: 1.6;
  }
`;

const Intro = styled.section`
  width: min(100%, 1440px);
  margin: auto;
  padding: 3.75rem 1rem 4.5rem;
  @media (min-width: 760px) {
    padding: clamp(7rem, 11vw, 11rem) 4vw;
  }
`;

const IntroText = styled.p`
  max-width: 18ch;
  margin-left: auto;
  margin-top: 1.25rem;
  color: var(--ink);
  font-size: clamp(2rem, 8.6vw, 6.3rem);
  line-height: 1.035;
  letter-spacing: -.045em;
  text-wrap: balance;

  em {
    display: block;
    margin-top: 1rem;
    color: var(--clay);
    font-weight: 400;
    line-height: 1.05;
  }

  @media (min-width: 760px) {
    margin-top: 1.75rem;
    font-size: clamp(3.8rem, 6.4vw, 6.3rem);
    line-height: .99;
  }
`;

const TopSales = styled.section`
  width: 100%;
  margin: auto;
  padding: clamp(1.75rem, 4vw, 5rem) 1rem clamp(3.5rem, 6vw, 6rem);
  background: var(--paper);
  @media (min-width: 760px) { padding-inline: 4vw; }
`;

const TopSalesHead = styled.div`
  width: min(100%, 1440px);
  margin-inline: auto;
  display: grid;
  gap: .65rem;
  margin-bottom: 1.25rem;
  @media (min-width: 820px) {
    grid-template-columns: 1fr minmax(280px, .55fr);
    align-items: end;
    margin-bottom: 2rem;
  }
`;

const TopSalesTitle = styled.h2`
  max-width: none;
  color: var(--ink);
  font-size: clamp(2.1rem, 9vw, 5.8rem);
  font-weight: 400;
  line-height: .86;
  letter-spacing: -.06em;
  white-space: nowrap;
`;

const TopSalesEyebrow = styled(Label)`
  display: block;
  margin-bottom: .7rem;
`;

const TopSalesLead = styled.p`
  max-width: 28rem;
  color: #5d625d;
  font: clamp(.98rem, 1.2vw, 1.1rem)/1.5 Arial, sans-serif;
`;

const TopSalesGrid = styled.div`
  width: min(100%, 1440px);
  margin-inline: auto;
  display: grid;
  gap: .75rem;
  @media (min-width: 760px) { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
`;

const TopSale = styled(NavLink)`
  min-height: 175px;
  display: grid;
  align-content: space-between;
  gap: .9rem;
  padding: 1.4rem 1.2rem 1.15rem;
  background: var(--card);
  color: var(--card-ink);
  @media (min-width: 760px) {
    min-height: 260px;
    padding: 1.6rem clamp(1.5rem, 3vw, 3.5rem);
  }
  @media (min-width: 1100px) { min-height: 290px; }
  &:hover { color: var(--card-ink); }
`;

const TopSaleNumber = styled.span`
  color: var(--card-label);
  font: 800 .64rem/1 Arial, sans-serif;
  letter-spacing: .14em;
`;

const TopSaleTitle = styled.h3`
  max-width: none;
  margin-top: .6rem;
  color: currentColor;
  font-size: clamp(1.55rem, 7.2vw, 3.8rem);
  font-weight: 400;
  line-height: .95;
  letter-spacing: -.055em;
  white-space: nowrap;
  transition: color .25s ease;
`;

const TopSaleHeader = styled.div`
  display: block;
`;

const TopSaleCta = styled.span`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  margin-top: .15rem;
  padding-top: .72rem;
  border-top: 1px solid color-mix(in srgb, currentColor 38%, transparent);
  color: currentColor;
  font: 800 .72rem/1 Arial, sans-serif;
  letter-spacing: .1em;
  text-transform: uppercase;
  svg { transition: transform .2s ease; }
  ${TopSale}:hover & svg { transform: translate(.18rem, -.18rem); }
`;

const GiftCallout = styled(NavLink)`
  display: grid;
  gap: 1.2rem;
  margin-top: 1.5rem;
  width: min(100%, 1440px);
  margin-inline: auto;
  padding: 1.5rem;
  background: var(--ink);
  color: #fff8ed;
  @media (min-width: 760px) {
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 2rem;
    padding: 2rem 2.4rem;
  }
  &:hover { background: #255548; color: #fff8ed; }
`;

const GiftCalloutTitle = styled.strong`
  display: block;
  font: 400 clamp(2rem, 3.5vw, 3.5rem)/.95 "Fraunces", Georgia, serif;
  letter-spacing: -.05em;
`;

const GiftCalloutText = styled.span`
  display: block;
  max-width: 36rem;
  margin-top: .55rem;
  color: rgba(255, 248, 237, .78);
  font: 1rem/1.5 Arial, sans-serif;
`;

const GiftCalloutCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: .75rem;
  width: fit-content;
  padding: .9rem 1rem;
  background: var(--fresh);
  color: var(--ink);
  font: 800 .72rem/1 Arial, sans-serif;
  letter-spacing: .1em;
  text-transform: uppercase;
  &::after { content: "↗"; font-size: 1rem; }
`;

const Paths = styled.section`
  width: min(100%, 1440px);
  margin: auto;
  padding: 0 1rem clamp(5rem, 10vw, 10rem);
  @media (min-width: 760px) { padding-inline: 4vw; }
`;

const Path = styled.article`
  display: grid;
  gap: 1.5rem;
  padding: 2.4rem 0;
  border-top: 1px solid rgba(23,63,53,.28);

  @media (min-width: 820px) {
    grid-template-columns: 46% 1fr;
    gap: clamp(3rem, 8vw, 9rem);
    padding: 5rem 0;
    &:nth-child(even) figure { order: 2; }
  }
`;

const PathImage = styled.figure`
  position: relative;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: #d8ccbb;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform .8s cubic-bezier(.2,.7,.2,1);
  }
  ${Path}:hover & img { transform: scale(1.025); }
`;

const PathIndex = styled.span`
  position: absolute;
  top: .8rem;
  left: .8rem;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  background: var(--light);
  color: var(--ink);
  border-radius: 50%;
  font-family: Arial, sans-serif;
  font-size: .68rem;
`;

const PathCopy = styled.div`
  align-self: center;
`;

const PathTitle = styled.h2`
  margin-top: .8rem;
  color: var(--ink);
  font-size: clamp(2.7rem, 7vw, 6.4rem);
  font-weight: 450;
  line-height: .9;
  letter-spacing: -.055em;
  text-wrap: balance;
`;

const PathText = styled.p`
  max-width: 34rem;
  margin-top: 1.3rem;
  color: #5d625d;
  font-family: Arial, sans-serif;
  font-size: clamp(.98rem, 1.6vw, 1.16rem);
  line-height: 1.7;
`;

const TextLink = styled(NavLink)`
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-top: 1.7rem;
  padding-bottom: .3rem;
  border-bottom: 1px solid currentColor;
  color: var(--ink);
  font-family: Arial, sans-serif;
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .09em;
  text-transform: uppercase;
`;

const Process = styled.section`
  padding: clamp(4.5rem, 10vw, 9rem) 1rem;
  background: var(--ink);
  color: #f8f1e6;
  @media (min-width: 760px) { padding-inline: 4vw; }
`;

const ProcessInner = styled.div`
  width: min(100%, 1440px);
  margin: auto;
`;

const ProcessTitle = styled.h2`
  max-width: 11ch;
  margin-top: 1rem;
  color: #f8f1e6;
  font-size: clamp(3rem, 7vw, 7rem);
  font-weight: 400;
  line-height: .9;
  letter-spacing: -.05em;
`;

const Steps = styled.ol`
  margin-top: 3.5rem;
  list-style: none;
`;

const Step = styled.li`
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 1rem;
  padding: 1.5rem 0;
  border-top: 1px solid rgba(248,241,230,.25);

  @media (min-width: 760px) { grid-template-columns: 80px .8fr 1fr; gap: 3rem; align-items: baseline; }
`;

const StepNo = styled.span`
  color: var(--fresh);
  font-family: Arial, sans-serif;
  font-size: .7rem;
`;

const StepTitle = styled.h3`
  color: #f8f1e6;
  font-size: clamp(1.55rem, 3.5vw, 3.2rem);
  font-weight: 400;
  line-height: 1;
`;

const StepText = styled.p`
  grid-column: 2;
  color: rgba(248,241,230,.65);
  font-family: Arial, sans-serif;
  font-size: .96rem;
  line-height: 1.6;
  @media (min-width: 760px) { grid-column: auto; }
`;

const EventFeature = styled.section`
  min-height: 78svh;
  position: relative;
  isolation: isolate;
  display: grid;
  align-items: end;
  padding: 5rem 1rem 2rem;
  color: #fff;
  @media (min-width: 760px) { padding: 7rem 4vw 4rem; }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -2;
    background: url(${event}) center / cover;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(180deg, transparent 20%, rgba(12,30,25,.82));
  }
`;

const EventCopy = styled.div`
  width: min(100%, 1440px);
  margin: auto;
`;

const EventTitle = styled.h2`
  max-width: 10ch;
  margin-top: 1rem;
  color: #fff;
  font-size: clamp(3rem, 9vw, 8rem);
  font-weight: 400;
  line-height: .86;
  letter-spacing: -.055em;
`;

const EventFoot = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-top: 2rem;
  @media (min-width: 760px) { grid-template-columns: 1fr auto; align-items: end; }
`;

const EventText = styled.p`
  max-width: 40ch;
  color: rgba(255,255,255,.8);
  font-family: Arial, sans-serif;
  line-height: 1.6;
`;

const GallerySection = styled.section`
  padding: clamp(5rem, 10vw, 9rem) 0;
  background: var(--light);
`;

const GalleryHead = styled.div`
  padding: 0 1rem;
  @media (min-width: 760px) { padding-inline: 4vw; }
`;

const GalleryTitle = styled.h2`
  max-width: 12ch;
  color: var(--ink);
  font-size: clamp(2.8rem, 7vw, 6.8rem);
  font-weight: 400;
  line-height: .92;
  letter-spacing: -.05em;
`;

const Gallery = styled.div`
  margin-top: 2.5rem;
  padding: 0 1rem 1.2rem;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 78vw;
  gap: .7rem;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scroll-snap-type: inline mandatory;
  scrollbar-width: thin;

  figure { scroll-snap-align: center; }
  figure:nth-child(2) { margin-top: 3rem; }
  figure:nth-child(3) { margin-top: 1.2rem; }
  figure:nth-child(4) { margin-top: 4rem; }

  @media (min-width: 760px) { grid-auto-columns: min(34vw, 480px); padding-inline: 4vw; gap: 1rem; }
`;

const GalleryImage = styled.figure`
  aspect-ratio: 3 / 4;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const PiecesSection = styled.section`
  padding: clamp(3.5rem, 7vw, 7rem) 1rem;
  background: #e5ded0;

  @media (min-width: 760px) { padding-inline: 4vw; }
`;

const PiecesInner = styled.div`
  width: min(100%, 1440px);
  margin: auto;
`;

const PiecesHead = styled.div`
  display: grid;
  gap: 1.4rem;
  padding-bottom: clamp(2.6rem, 5vw, 5.25rem);
  border-bottom: 1px solid rgba(23, 63, 53, .3);

  @media (min-width: 760px) {
    grid-template-columns: 1fr minmax(240px, .38fr);
    align-items: end;
  }
`;

const PiecesTitle = styled.h2`
  max-width: 9ch;
  color: var(--ink);
  font-size: clamp(3.7rem, 9.2vw, 9.5rem);
  font-weight: 400;
  line-height: .78;
  letter-spacing: -.075em;
  text-wrap: balance;

  em { color: var(--clay); font-style: italic; }
`;

const PiecesIntro = styled.p`
  max-width: 28ch;
  color: #506057;
  font: clamp(.95rem, 1.35vw, 1.12rem)/1.55 Arial, sans-serif;
`;

const PiecesViewport = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 90vw;
  gap: .85rem;
  margin-top: 1.4rem;
  padding: 0 .15rem 1rem;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;

  @media (min-width: 760px) {
    grid-auto-columns: min(84vw, 1180px);
    gap: 1.35rem;
    margin-top: 2rem;
  }
`;

const PiecesPanel = styled.div`
  height: min(140vw, 650px);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(8, 1fr);
  gap: .55rem;
  scroll-snap-align: start;

  @media (min-width: 760px) {
    height: min(48vw, 610px);
    grid-template-columns: repeat(12, minmax(0, 1fr));
    grid-template-rows: repeat(8, 1fr);
    gap: 1rem;
  }

  &.square-panel {
    height: min(156vw, 720px);
    grid-template-rows: repeat(9, 1fr);
  }

  &.square-panel > figure:nth-child(1) { grid-column: 1; grid-row: 1 / span 4; }
  &.square-panel > figure:nth-child(2) { grid-column: 2; grid-row: 1 / span 3; }
  &.square-panel > figure:nth-child(3) { grid-column: 1 / span 2; grid-row: 5 / span 5; }
  &.square-panel > figure:nth-child(2) img { object-fit: contain; background: #d8ccbb; }

  &.first-panel { height: min(120vw, 620px); grid-template-rows: repeat(8, 1fr); }
  &.first-panel > figure:nth-child(1), &.first-panel > figure:nth-child(2) { grid-row: 1 / span 5; }
  &.first-panel > figure:nth-child(3) { grid-column: 1; grid-row: 6 / span 3; }
  &.first-panel > figure:nth-child(4) { grid-column: 2; grid-row: 6 / span 3; }

  @media (min-width: 760px) {
    &.square-panel { height: min(55vw, 750px); grid-template-rows: repeat(8, 1fr); }
    &.square-panel > figure:nth-child(1) { grid-column: 1 / span 5; grid-row: 1 / span 8; }
    &.square-panel > figure:nth-child(2) { grid-column: 6 / span 3; grid-row: 1 / span 3; }
    &.square-panel > figure:nth-child(3) { grid-column: 9 / span 4; grid-row: 1 / span 4; }
    &.first-panel { height: min(55vw, 750px); grid-template-rows: repeat(8, 1fr); }
    &.first-panel > figure:nth-child(1) { grid-column: 1 / span 4; grid-row: 1 / span 8; }
    &.first-panel > figure:nth-child(2) { grid-column: 5 / span 4; grid-row: 1 / span 8; }
    &.first-panel > figure:nth-child(3) { grid-column: 9 / span 4; grid-row: 1 / span 5; }
    &.first-panel > figure:nth-child(4) { grid-column: 9 / span 4; grid-row: 6 / span 3; }
  }
`;

const PanelPiece = styled.figure`
  position: relative;
  overflow: hidden;
  background: #c9bca8;

  &:nth-child(1) { grid-column: 1; grid-row: 1 / span 5; }
  &:nth-child(2) { grid-column: 2; grid-row: 1 / span 5; }
  &:nth-child(3) { grid-column: 1; grid-row: 6 / span 3; }
  &:nth-child(4) { grid-column: 2; grid-row: 6 / span 3; }
  &:first-child:nth-last-child(2) { grid-column: 1; grid-row: 1 / span 6; }
  &:nth-child(2):last-child { grid-column: 2; grid-row: 1 / span 6; }

  @media (min-width: 760px) {
    &:nth-child(1) { grid-column: 1 / span 4; grid-row: 1 / span 8; }
    &:nth-child(2) { grid-column: 5 / span 4; grid-row: 1 / span 8; }
    &:nth-child(3) { grid-column: 9 / span 4; grid-row: 1 / span 4; }
    &:nth-child(4) { grid-column: 9 / span 4; grid-row: 5 / span 4; }
    &:first-child:nth-last-child(2) { grid-column: 1 / span 6; grid-row: 1 / span 8; }
    &:nth-child(2):last-child { grid-column: 7 / span 6; grid-row: 1 / span 8; }
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform .8s cubic-bezier(.2,.7,.2,1), filter .5s ease;
  }

  figcaption {
    position: absolute;
    right: .55rem;
    bottom: .55rem;
    left: .55rem;
    width: fit-content;
    max-width: calc(100% - 1.1rem);
    padding: .42rem .55rem;
    background: rgba(250, 246, 237, .93);
    color: var(--ink);
    font: 700 clamp(.5rem, .65vw, .62rem)/1.2 Arial, sans-serif;
    letter-spacing: .08em;
    text-transform: uppercase;
  }

  &:nth-child(odd) figcaption { top: .55rem; bottom: auto; }
  &.caption-bottom figcaption { top: auto; bottom: .55rem; }

  @media (hover: hover) { &:hover img, &:hover video { transform: scale(1.045); filter: saturate(1.06); } }
`;

const Lightbox = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(10, 22, 18, .9);
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255,255,255,.5);
  border-radius: 50%;
  background: transparent;
  color: #fff;
`;

const LightboxMedia = styled.div`
  width: min(100%, 1100px);
  max-height: calc(100svh - 4rem);

  img, video { width: 100%; max-height: calc(100svh - 4rem); object-fit: contain; }
`;

const FaqSection = styled.section`
  padding: clamp(5rem, 10vw, 9rem) 1rem;
  background: var(--paper);
  @media (min-width: 760px) { padding-inline: 4vw; }
`;

const FaqInner = styled.div`
  width: min(100%, 1440px);
  margin: auto;
  display: grid;
  gap: 3rem;

  @media (min-width: 880px) {
    grid-template-columns: minmax(280px, .72fr) minmax(0, 1.28fr);
    gap: clamp(4rem, 9vw, 10rem);
  }
`;

const FaqTitle = styled.h2`
  max-width: 9ch;
  margin-top: 1rem;
  color: var(--ink);
  font-size: clamp(3rem, 7vw, 7rem);
  font-weight: 400;
  line-height: .88;
  letter-spacing: -.055em;
`;

const Questions = styled.div`
  border-bottom: 1px solid rgba(23, 63, 53, .28);
`;

const Question = styled.details`
  border-top: 1px solid rgba(23, 63, 53, .28);

  &[open] summary svg { transform: rotate(45deg); }
  &[open] p { animation: ${enter} .35s ease both; }
`;

const QuestionTitle = styled.summary`
  min-height: 68px;
  display: grid;
  grid-template-columns: 30px 1fr 24px;
  gap: .75rem;
  align-items: center;
  padding: 1rem 0;
  color: var(--ink);
  font-size: clamp(1.2rem, 3.8vw, 1.75rem);
  line-height: 1.15;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker { display: none; }
  span:first-child {
    color: var(--clay);
    font: .68rem/1 Arial, sans-serif;
  }
  svg { transition: transform .3s ease; }
`;

const Answer = styled.p`
  max-width: 46rem;
  padding: 0 2rem 1.5rem 2.85rem;
  color: #3f5149;
  font: clamp(1rem, 1.2vw, 1.16rem)/1.68 Arial, sans-serif;
`;

const Practical = styled.section`
  display: grid;
  background: var(--clay);
  @media (min-width: 820px) { grid-template-columns: 1fr 1fr; }
`;

const PracticalImage = styled.img`
  width: 100%;
  min-height: 420px;
  height: 100%;
  object-fit: cover;
`;

const PracticalCopy = styled.div`
  padding: clamp(3rem, 8vw, 8rem) 1rem;
  align-self: center;
  @media (min-width: 760px) { padding-inline: 6vw; }
`;

const PracticalTitle = styled.h2`
  max-width: 9ch;
  color: #fff8ed;
  font-size: clamp(3rem, 6vw, 6rem);
  font-weight: 400;
  line-height: .9;
  letter-spacing: -.05em;
`;

const Facts = styled.div`
  margin: 2rem 0;
`;

const Fact = styled.p`
  padding: .75rem 0;
  border-top: 1px solid rgba(255,255,255,.38);
  color: #fff8ed;
  font-family: Arial, sans-serif;
  font-size: .82rem;
  letter-spacing: .04em;
`;

const Home = () => {
  const { t, i18n } = useTranslation();
  const [lightbox, setLightbox] = useState(null);
  const lang = i18n.language?.startsWith("ca") ? "ca" : "es";
  const siteUrl = "https://eltalleraguaymanto.com";
  const seo = lang === "ca" ? {
    title: "Taller de ceràmica a Vilanova del Vallès | El Taller de Aguaymanto",
    description: "Experiències de ceràmica per crear, compartir i baixar el ritme, a prop de Granollers.",
    locale: "ca_ES",
  } : {
    title: "Taller de cerámica en Vilanova del Vallès | El Taller de Aguaymanto",
    description: "Experiencias de cerámica para crear, compartir y bajar el ritmo, cerca de Granollers.",
    locale: "es_ES",
  };
  const paths = [
    { image: painting, label: "Ceramic studio", title: t("home.feature2.title"), text: t("home.feature2.text"), link: "/experiencias-ceramica-granollers", cta: t("home.ctaExperiencias") },
    { image: event, label: "Private gatherings", title: t("home.feature1.title"), text: t("home.feature1.text"), link: "/eventos", cta: t("home.ctaEventos") },
    { image: gift, label: "Gift edition", title: t("home.feature3.title"), text: t("home.feature3.text"), link: "/regala-pinta-tu-ceramica-granollers", cta: t("home.ctaRegala") },
  ];
  const topSales = lang === "ca" ? [
    { title: "Pinta la teva ceràmica", label: "UNA SESSIÓ", card: "#f2c879", ink: "#173f35", labelColor: "#a14f31", link: "/pinta-tu-ceramica" },
    { title: "Crea la teva peça", label: "DUES SESSIONS", card: "#df9b78", ink: "#173f35", labelColor: "#fff8ed", link: "/crea-tu-ceramica-y-decorala" },
    { title: "Ceràmica regular", label: "CLASSES SETMANALS", card: "#17483d", ink: "#fff8ed", labelColor: "#d9e7a8", link: "/clases-ceramica" },
    { title: "Ceràmica i vi", label: "PLA EN GRUP", card: "#d9e7a8", ink: "#173f35", labelColor: "#a14f31", link: "/ceramica-y-vino" },
  ] : [
    { title: "Pinta tu cerámica", label: "UNA SESIÓN", card: "#f2c879", ink: "#173f35", labelColor: "#a14f31", link: "/pinta-tu-ceramica" },
    { title: "Crea tu pieza", label: "DOS SESIONES", card: "#df9b78", ink: "#173f35", labelColor: "#fff8ed", link: "/crea-tu-ceramica-y-decorala" },
    { title: "Cerámica regular", label: "CLASES SEMANALES", card: "#17483d", ink: "#fff8ed", labelColor: "#d9e7a8", link: "/clases-ceramica" },
    { title: "Cerámica y vino", label: "PLAN EN GRUPO", card: "#d9e7a8", ink: "#173f35", labelColor: "#a14f31", link: "/ceramica-y-vino" },
  ];
  const steps = [
    [t("home.process.step1.title"), t("home.process.step1.text")],
    [t("home.process.step2.title"), t("home.process.step2.text")],
    [t("home.process.step3.title"), t("home.process.step3.text")],
  ];
  const pieces = lang === "ca" ? [
    [colourfulPlate, "Plat de colors", "Peces úniques"],
    [greenPlate, "Plat geomètric verd", "Pintat a mà"],
    [finishedPieces, "Selecció de peces acabades", "El Taller"],
    [trayFlowers, "Safata floral", "Pintat a mà"],
    [paintedGlasses, "Gots pintats", "Peces úniques"],
    [pinkBowl, "Bol blau fet a mà", "Classe setmanal"],
    [blueBowl, "Bol rosa de lunars", "Peces úniques"],
    [tableCeramics, "Dues alumnes creant ceràmica a les classes setmanals", "Classes setmanals de ceràmica", undefined, "Alumnes d'El Taller"],
    [brushProcess, "Pintant un plat", "En procés"],
    [whalePlate, "Plat de balena", "Pintat a mà"],
    [paintedPlates, "Quadre de rajoles pintades", "Peces úniques"],
    [ceramicProcessOne, "Procés de ceràmica", "En procés"],
    [ceramicProcessTwo, "Procés de ceràmica", "En procés"],
    [decoratingPlate, "Decorant un plat", "Pintat a mà"],
    [ceramicPalette, "Paleta de colors", "El Taller"],
    [kilnWhalePlate, "Plat de balena al forn", "En procés"],
    [handPinkBowl, "Bol rosa fet a mà", "Peces úniques"],
    [lemonPlate, "Plat de llimones", "Pintat a mà"],
    [shelfWhalePlate, "Plat de balena", "Peces úniques"],
    [paintingCeramicsVideo, "Pintant ceràmica", "En procés", "video"],
    [finishedVaseVideo, "Gerro acabat", "Peça única", "video"],
    [finishedCupVideo, "Tassa acabada", "Peça única", "video"],
  ] : [
    [colourfulPlate, "Plato colorido", "Piezas únicas"],
    [greenPlate, "Plato geométrico verde", "Pintado a mano"],
    [finishedPieces, "Selección de piezas terminadas", "El Taller"],
    [trayFlowers, "Bandeja floral", "Pintado a mano"],
    [paintedGlasses, "Vasos pintados", "Piezas únicas"],
    [pinkBowl, "Cuenco azul hecho a mano", "Clase semanal"],
    [blueBowl, "Cuenco rosa de lunares", "Piezas únicas"],
    [tableCeramics, "Dos alumnas creando cerámica en las clases semanales", "Clases semanales de cerámica", undefined, "Alumnas de El Taller"],
    [brushProcess, "Pintando un plato", "En proceso"],
    [whalePlate, "Plato de ballena", "Pintado a mano"],
    [paintedPlates, "Cuadro de azulejos pintados", "Piezas únicas"],
    [ceramicProcessOne, "Proceso de cerámica", "En proceso"],
    [ceramicProcessTwo, "Proceso de cerámica", "En proceso"],
    [decoratingPlate, "Decorando un plato", "Pintado a mano"],
    [ceramicPalette, "Paleta de colores", "El Taller"],
    [kilnWhalePlate, "Plato de ballena en el horno", "En proceso"],
    [handPinkBowl, "Cuenco rosa hecho a mano", "Piezas únicas"],
    [lemonPlate, "Plato de limones", "Pintado a mano"],
    [shelfWhalePlate, "Plato de ballena", "Piezas únicas"],
    [paintingCeramicsVideo, "Pintando cerámica", "En proceso", "video"],
    [finishedVaseVideo, "Jarrón terminado", "Pieza única", "video"],
    [finishedCupVideo, "Taza terminada", "Pieza única", "video"],
  ];
  const piecePages = [
    [pieces[0], pieces[2], pieces[1], pieces[6]],
    [pieces[3], pieces[19], pieces[4], pieces[5]],
    [pieces[10], pieces[20], pieces[7], pieces[8]],
    [pieces[9], pieces[21], pieces[11], pieces[12]],
    [pieces[13], pieces[14], pieces[15], pieces[16]],
    [pieces[17], pieces[18], pieces[19]],
  ];
  const ribbonItems = [
    [Palette, "Cerámica"],
    [Paintbrush, "Creatividad"],
    [Coffee, "Coffee corner"],
    [Palette, "Comunidad"],
    [Paintbrush, "Calma"],
    [Coffee, "A 8 min de Granollers"],
  ];
  const faqs = lang === "ca" ? [
    ["Necessito experiència prèvia?", "No. Les sessions estan pensades perquè qualsevol persona pugui gaudir creant. T'acompanyem amb tècniques senzilles i al teu ritme."],
    ["Quant dura l'experiència?", "Aproximadament entre una hora i mitja i dues hores, segons l'activitat i el ritme de cada persona."],
    ["Què inclou la reserva?", "És una classe guiada per nosaltres, expertes en ceràmica i pintura. Us acompanyem durant tot el procés perquè només hàgiu de venir a crear i gaudir. Inclou tots els materials i accés gratuït al coffee corner."],
    ["Quan podré recollir la meva peça?", "Després de pintar-la, nosaltres ens encarreguem del forn. T'avisarem quan estigui acabada i llesta per recollir."],
    ["Puc venir amb un grup?", "Sí. Organitzem experiències per a amigues, famílies, celebracions i equips en grups de fins a 10 persones."],
    ["On és El Taller?", "Som a la urbanització Les Roquetes, a Vilanova del Vallès, aproximadament a vuit minuts de Granollers, en un entorn tranquil i envoltat de verd."],
  ] : [
    ["¿Necesito experiencia previa?", "No. Las sesiones están pensadas para que cualquier persona disfrute creando. Te acompañamos con técnicas sencillas y a tu ritmo."],
    ["¿Cuánto dura la experiencia?", "Aproximadamente entre una hora y media y dos horas, según la actividad y el ritmo de cada persona."],
    ["¿Qué incluye la reserva?", "Es una clase guiada por nosotras, expertas en cerámica y pintura. Os acompañamos durante todo el proceso para que solo tengáis que venir a crear y disfrutar. Incluye todos los materiales y acceso gratuito al coffee corner."],
    ["¿Cuándo podré recoger mi pieza?", "Después de pintarla, nosotras nos encargamos del horneado. Te avisaremos cuando esté terminada y lista para recoger."],
    ["¿Puedo venir con un grupo?", "Sí. Organizamos experiencias para amigas, familias, celebraciones y equipos en grupos de hasta 10 personas."],
    ["¿Dónde está El Taller?", "Estamos en la Urbanización Les Roquetes, en Vilanova del Vallès, a unos ocho minutos de Granollers, en un entorno tranquilo y rodeado de verde."],
  ];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ArtsAndEntertainment"],
    name: "El Taller de Aguaymanto",
    url: siteUrl,
    image: `${siteUrl}/logo.png`,
    description: seo.description,
    telephone: "+34683704011",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Vilanova del Vallès",
      addressRegion: "Barcelona",
      addressCountry: "ES",
    },
    sameAs: ["https://www.instagram.com/eltaller.artes"],
  };

  const trimVideo = (e) => {
    const video = e.currentTarget;
    if (Number.isFinite(video.duration) && video.currentTime >= video.duration - 2) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  };
  const openMedia = (src, alt, type = "image") => setLightbox({ src, alt, type });
  const handleMediaKey = (event, src, alt, type) => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openMedia(src, alt, type); }
  };

  return (
    <Main>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`${siteUrl}/`} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:locale" content={seo.locale} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="El Taller de Aguaymanto" />
        <meta property="og:url" content={`${siteUrl}/`} />
        <meta property="og:image" content={`${siteUrl}/logo.png`} />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <Hero>
        <HeroVideo autoPlay muted loop playsInline preload="metadata" onTimeUpdate={trimVideo}><source src={bannerVideo} type="video/mp4" /></HeroVideo>
        <HeroShade />
        <HeroGrid>
          <HeroCopy><Label>Vilanova del Vallès · Barcelona</Label><HeroTitle>{t("home.title")}</HeroTitle></HeroCopy>
          <HeroMeta><HeroLead>{t("home.subtitle")}</HeroLead><PrimaryLink to="/experiencias-ceramica-granollers">{t("home.ctaButton")}<LinkArrow aria-hidden="true" /></PrimaryLink></HeroMeta>
        </HeroGrid>
      </Hero>

      <LocationLine aria-hidden="true"><MovingLine>{[0, 1].map((group) => <span key={group}>{ribbonItems.map(([Icon, text], index) => <i key={`${group}-${text}-${index}`}><Icon />{text}</i>)}</span>)}</MovingLine></LocationLine>

      <Intro><Label style={{ color: "var(--clay)" }}>01 — {t("home.whatYouCanFind")}</Label><IntroText>{t("quienes.p1")} <em>{t("quienes.linea3")}</em></IntroText></Intro>

      <TopSales>
        <TopSalesHead>
          <div><TopSalesEyebrow>02 — {lang === "ca" ? "Les més escollides" : "Las más elegidas"}</TopSalesEyebrow><TopSalesTitle>{lang === "ca" ? "Tria i reserva." : "Elige y reserva."}</TopSalesTitle></div>
          <TopSalesLead>{lang === "ca" ? "Descobreix-la i reserva la teva plaça." : "Descúbrela y reserva tu plaza."}</TopSalesLead>
        </TopSalesHead>
        <TopSalesGrid>{topSales.map((item, index) => <TopSale key={item.title} to={item.link} style={{ "--card": item.card, "--card-ink": item.ink, "--card-label": item.labelColor }}><TopSaleHeader><TopSaleNumber>0{index + 1} · {item.label}</TopSaleNumber><TopSaleTitle>{item.title}</TopSaleTitle></TopSaleHeader><TopSaleCta><span>{lang === "ca" ? "Veure experiència" : "Ver experiencia"}</span><ArrowUpRight size={20} strokeWidth={1.5} aria-hidden="true" /></TopSaleCta></TopSale>)}</TopSalesGrid>
        <GiftCallout to="/regala-pinta-tu-ceramica-granollers"><div><GiftCalloutTitle>{lang === "ca" ? "Vols regalar una activitat?" : "¿Quieres regalar una actividad?"}</GiftCalloutTitle><GiftCalloutText>{lang === "ca" ? "Primer tria l'experiència, després personalitza el voucher amb el teu missatge i regala'l." : "Primero elige la experiencia, después personaliza el voucher con tu mensaje y regálalo."}</GiftCalloutText></div><GiftCalloutCta>{lang === "ca" ? "Tria una activitat" : "Elige una actividad"}</GiftCalloutCta></GiftCallout>
      </TopSales>

      <Paths>{paths.map((item, index) => <Path key={item.title}><PathImage><img src={item.image} alt="" loading="lazy" /><PathIndex>0{index + 1}</PathIndex></PathImage><PathCopy><Label style={{ color: "var(--clay)" }}>{item.label}</Label><PathTitle>{item.title}</PathTitle><PathText>{item.text}</PathText><TextLink to={item.link}>{item.cta}<LinkArrow aria-hidden="true" /></TextLink></PathCopy></Path>)}</Paths>

      <Process><ProcessInner><Label style={{ color: "var(--fresh)" }}>02 — Studio ritual</Label><ProcessTitle>{t("home.process.title")}</ProcessTitle><Steps>{steps.map(([title, text], index) => <Step key={title}><StepNo>0{index + 1}</StepNo><StepTitle>{title.replace(/^\d+\.\s*/, "")}</StepTitle><StepText>{text}</StepText></Step>)}</Steps></ProcessInner></Process>

      <PiecesSection>
        <PiecesInner>
          <PiecesHead>
            <div><Label style={{ color: "var(--clay)" }}>03 — Made in El Taller</Label><PiecesTitle>{lang === "ca" ? <>Peces <em>creades</em> aquí.</> : <>Piezas <em>creadas</em> aquí.</>}</PiecesTitle></div>
            <PiecesIntro>{lang === "ca" ? "Cada peça porta el gest, el color i la història de qui l'ha imaginada. Una petita selecció sortida del nostre taller." : "Cada pieza lleva el gesto, el color y la historia de quien la imaginó. Una pequeña selección salida de nuestro taller."}</PiecesIntro>
          </PiecesHead>
          <PiecesViewport aria-label={lang === "ca" ? "Galeria de peces creades a El Taller" : "Galería de piezas creadas en El Taller"}>
            {piecePages.map((page, pageIndex) => <PiecesPanel key={pageIndex} className={pageIndex === 0 ? "first-panel" : pageIndex === 5 ? "square-panel" : ""}>{page.map(([media, alt, note, type, label]) => <PanelPiece key={alt} className={label ? "caption-bottom" : ""} role="button" tabIndex="0" onClick={() => openMedia(media, alt, type)} onKeyDown={(event) => handleMediaKey(event, media, alt, type)}>{type === "video" ? <video src={media} aria-label={alt} autoPlay muted loop playsInline preload="metadata" /> : <img src={media} alt={alt} loading="lazy" decoding="async" />}<figcaption>{label || `${alt} · ${note}`}</figcaption></PanelPiece>)}</PiecesPanel>)}
          </PiecesViewport>
        </PiecesInner>
      </PiecesSection>

      <EventFeature><EventCopy><Label>04 — {t("eventos.titulo")}</Label><EventTitle>{t("eventos.cards.privados.title")}</EventTitle><EventFoot><EventText>{t("eventos.subtexto")} {t("eventos.cards.team.texto")}</EventText><PrimaryLink to="/eventos">{t("eventos.cta")}<LinkArrow aria-hidden="true" /></PrimaryLink></EventFoot></EventCopy></EventFeature>

      <GallerySection><GalleryHead><Label style={{ color: "var(--clay)" }}>04 — Inside the studio</Label><GalleryTitle>{t("home.galleryTitle")}</GalleryTitle></GalleryHead><Gallery aria-label={t("home.galleryTitle")}><GalleryImage role="button" tabIndex="0" onClick={() => openMedia(studio, t("home.alts.pinta"))}><img src={studio} alt={t("home.alts.pinta")} loading="lazy" /></GalleryImage><GalleryImage role="button" tabIndex="0" onClick={() => openMedia(detail, t("home.alts.regalo"))}><img src={detail} alt={t("home.alts.regalo")} loading="lazy" /></GalleryImage><GalleryImage role="button" tabIndex="0" onClick={() => openMedia(studioInterior, "Interior de El Taller")}><img src={studioInterior} alt="Interior de El Taller" loading="lazy" /></GalleryImage><GalleryImage role="button" tabIndex="0" onClick={() => openMedia(studioShelves, "Estantería de cerámica de El Taller")}><img src={studioShelves} alt="Estantería de cerámica de El Taller" loading="lazy" /></GalleryImage><GalleryImage role="button" tabIndex="0" onClick={() => openMedia(group, t("home.alts.eventos"))}><img src={group} alt={t("home.alts.eventos")} loading="lazy" /></GalleryImage><GalleryImage role="button" tabIndex="0" onClick={() => openMedia(space, t("home.alts.highlight"))}><img src={space} alt={t("home.alts.highlight")} loading="lazy" /></GalleryImage></Gallery></GallerySection>

      <FaqSection id="preguntas-frecuentes"><FaqInner><div><Label style={{ color: "var(--clay)" }}>05 — Before you visit</Label><FaqTitle>{lang === "ca" ? "Abans de venir." : "Antes de venir."}</FaqTitle></div><Questions>{faqs.map(([question, answer], index) => <Question key={question}><QuestionTitle><span>0{index + 1}</span><span>{question}</span><Plus aria-hidden="true" size={20} strokeWidth={1.5} /></QuestionTitle><Answer>{answer}</Answer></Question>)}</Questions></FaqInner></FaqSection>

      <Practical><PracticalImage src={space} alt="Interior de El Taller de Aguaymanto" loading="lazy" /><PracticalCopy><Label style={{ color: "#fff8ed" }}>06 — Visit the studio</Label><PracticalTitle>{t("home.process.calmStrong")}</PracticalTitle><Facts><Fact>Urbanización Les Roquetes · Vilanova del Vallès</Fact><Fact>Aproximadamente a 8 min de Granollers</Fact><Fact>Sesiones guiadas · Material incluido · Coffee corner</Fact></Facts><PrimaryLink to="/experiencias-ceramica-granollers">{t("home.ctaButton")}<LinkArrow aria-hidden="true" /></PrimaryLink></PracticalCopy></Practical>
      {lightbox && <Lightbox role="dialog" aria-modal="true" aria-label={lightbox.alt} onClick={() => setLightbox(null)}><LightboxClose type="button" aria-label={t("home.close")} onClick={() => setLightbox(null)}><X size={21} /></LightboxClose><LightboxMedia onClick={(event) => event.stopPropagation()}>{lightbox.type === "video" ? <video src={lightbox.src} aria-label={lightbox.alt} controls autoPlay playsInline /> : <img src={lightbox.src} alt={lightbox.alt} />}</LightboxMedia></Lightbox>}
    </Main>
  );
};

export default Home;
